import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Offcanvas } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  closeIaDrawer,
  iaAnswerDone,
  iaAnswerError,
  iaTokenReceived,
  newIaDiscussion,
  showIaHistory,
} from "../../REDUX/actions/ia";
import IaMessageList from "./IaMessageList";
import IaComposer from "./IaComposer";
import IaDiscussionList from "./IaDiscussionList";
import { IaAssistantSvg, IaHistorySvg, IaPlusSvg } from "./IaIcons";
import "./Ia.css";

/**
 * Panneau lateral de l'assistant.
 *
 * Monte une seule fois dans App.js, hors du routeur : c'est le seul moyen de le
 * rendre accessible aux roles 3 et 4, qui disposent d'un routeur separe reduit a
 * une unique route.
 */
const IaDrawer = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");

  const { drawerOpen, showHistory, currentDiscussionId } = useSelector(
    (state) => state.iaReducer
  );
  const { socket } = useSelector((state) => state.socketReducer);

  /**
   * Abonnement aux evenements de streaming.
   *
   * Meme convention que le chat SAV : join-room a l'ouverture, off + leave-room
   * au nettoyage. Ces evenements transportent une charge utile (le token) et non
   * un simple identifiant, car re-interroger l'API a chaque token n'aurait
   * aucun sens.
   */
  useEffect(() => {
    if (!socket || !currentDiscussionId) return undefined;

    const room = `ia-${currentDiscussionId}`;
    socket.emit("join-room", room);

    const onToken = (payload) => dispatch(iaTokenReceived(payload));
    const onDone = (payload) => dispatch(iaAnswerDone(payload));
    const onError = (payload) => dispatch(iaAnswerError(payload));

    // Pas d'evenement de sources : le back ne les diffuse plus au navigateur.
    socket.on("ia-token", onToken);
    socket.on("ia-done", onDone);
    socket.on("ia-error", onError);

    return () => {
      socket.off("ia-token", onToken);
      socket.off("ia-done", onDone);
      socket.off("ia-error", onError);
      socket.emit("leave-room", room);
    };
  }, [socket, currentDiscussionId, dispatch]);

  return (
    <Offcanvas
      show={drawerOpen}
      onHide={() => dispatch(closeIaDrawer())}
      placement="end"
      /* Le voile est actif pour capter le clic hors du tiroir et le refermer,
         mais rendu totalement transparent (voir .iaBackdrop) : l'application
         reste visible derriere, comme avant. `scroll` empeche le blocage du
         defilement de la page que le voile imposerait par defaut. */
      backdrop
      backdropClassName="iaBackdrop"
      scroll
      className="iaDrawer"
    >
      <div className="iaDrawerHeader alignH justify-content-between px-3 py-3 border-bottom">
        <div className="alignH gap-2">
          <IaAssistantSvg size={24} />
          <span className="f16 sBold iaBlue">{t("AI Assistant")}</span>
        </div>

        <div className="alignH gap-1">
          <button
            type="button"
            className="iaHeaderBtn border-0 bg-transparent"
            title={t("New conversation")}
            onClick={() => dispatch(newIaDiscussion())}
          >
            <IaPlusSvg />
          </button>
          <button
            type="button"
            className={`iaHeaderBtn border-0 bg-transparent ${
              showHistory ? "iaHeaderBtnActive" : ""
            }`}
            title={t("History")}
            onClick={() => dispatch(showIaHistory(!showHistory))}
          >
            <IaHistorySvg />
          </button>
          <button
            type="button"
            className="iaHeaderBtn border-0 bg-transparent"
            title={t("Close")}
            onClick={() => dispatch(closeIaDrawer())}
          >
            <i className="bi bi-x-lg iaBlue f16" />
          </button>
        </div>
      </div>

      {showHistory ? (
        <IaDiscussionList />
      ) : (
        <>
          <IaMessageList />
          <IaComposer />
          <p className="f10 normal text-lightBlack text-center px-3 pb-2 mb-0">
            {t(
              "Answers are based on the technical documentation and the web, and may contain errors. Conversations can be reviewed by an administrator."
            )}
          </p>
        </>
      )}
    </Offcanvas>
  );
};

export default IaDrawer;
